/**
 * @file header.component.tsx
 * @description Компонент заголовка модального окна для Task Manager приложения, который обеспечивает стандартизированную шапку с заголовком, кнопками управления и контекстным меню. Включает кнопку закрытия, кнопку сохранения с индикатором загрузки, кнопку обновления формы, контекстное меню с дополнительными действиями и поддержку настраиваемых лейблов. Используется как переиспользуемый компонент для различных типов модальных окон в системе.
 * @author AI-Generated | Human-Reviewed
 * @created 2025-01-15
 * @updated 2025-01-16
 * @dependencies ['react', '@consta/uikit/Text', '@consta/uikit/Button', '@consta/icons/IconClose', '@consta/icons/IconRestart', '@consta/icons/IconKebab', '@consta/uikit/ContextMenu']
 * @related-files ['src/components/ui/layout/modal/modal.component.tsx', 'src/features/object-form/object-form.component.tsx']
 */

import { useRef, useState } from 'react';
import { Text } from '@consta/uikit/Text';
import { Button } from '@consta/uikit/Button';
import { IconClose } from '@consta/icons/IconClose';
import { IconRestart } from '@consta/icons/IconRestart';
import { IconKebab } from '@consta/icons/IconKebab';
import { ContextMenu } from '@consta/uikit/ContextMenu';

/**
 * Props для компонента заголовка модального окна
 */
interface IHeaderProps {
    /** Текст заголовка модального окна */
    title: string;
    /** Обработчик клика по кнопке закрытия */
    closeBtnClickHandler?: () => void;
    /** Обработчик клика по кнопке сохранения */
    saveBtnClickHandler?: () => void;
    /** Обработчик клика по кнопке обновления формы */
    refreshFormClickHandler?: () => void;
    /** Текст кнопки сохранения */
    saveBtnTitle?: string;
    /** Флаг состояния загрузки */
    isFetching?: boolean;
}

export const Header = ({
    title,
    closeBtnClickHandler,
    saveBtnClickHandler,
    refreshFormClickHandler,
    saveBtnTitle = 'Сохранить',
    isFetching = false,
    ...props
}: IHeaderProps) => {
    const [isShowContext, setIsShowContext] = useState(false);
    const extraButtonRef = useRef(null);

    return (
        <div className="mb-[0.8127vmin] flex flex-row justify-between flex-align-center">
            <Text size="l" view="primary" className="font-medium leading-[150%] content-center" {...props}>
                {title}
            </Text>

            <div className="flex flex-row flex-nowrap items-center gap-[0.8127vmin]">
                {refreshFormClickHandler && (
                    <Button
                        label="Дополнительные действия"
                        size="s"
                        view="ghost"
                        iconRight={IconKebab}
                        iconSize="xs"
                        onlyIcon
                        onClick={() => setIsShowContext(true)}
                        ref={extraButtonRef}
                    />
                )}

                {saveBtnClickHandler && (
                    <Button
                        label={saveBtnTitle}
                        onClick={saveBtnClickHandler}
                        loading={isFetching}
                        className="min-w-[12.1905vmin]"
                        size="s"
                    />
                )}

                {closeBtnClickHandler && (
                    <Button
                        view="clear"
                        label="Закрыть"
                        size="s"
                        iconRight={IconClose}
                        onlyIcon
                        onClick={closeBtnClickHandler}
                    />
                )}
            </div>

            <ContextMenu
                items={[
                    {
                        label: 'Обновить данные',
                        icon: <IconRestart size="xs" view="brand" />,
                        onClick: () => {
                            refreshFormClickHandler && refreshFormClickHandler();
                            setIsShowContext(false);
                        }
                    }
                ]}
                isOpen={isShowContext}
                anchorRef={extraButtonRef}
                getItemLeftSide={(item) => item.icon}
                onClickOutside={() => setIsShowContext(false)}
                direction="downStartRight"
                style={{ pointerEvents: 'auto' }}
                className="absolute"
            />
        </div>
    );
};